import { Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar, Header, Loader } from "@workspace/shared";
import "../style.css";
import {
  CommissionRules,
  Home,
  Partners,
  RunPayout,
  Target,
  PaymentLogs,
} from "@workspace/shared";
import { ROUTES } from "@/lib/routes";
import { removeAuthData, useUserDetails } from "@/redux/function";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";

const navItems = [
  {
    label: "Dashboard",
    path: "/",
    icon: Home,
    matchPaths: ["/"],
    permission: { module_name: "Default", permission: "Default" },
  },
  {
    label: "Commission Rules",
    path: ROUTES.COMMISSION_RULES.BASE,
    icon: CommissionRules,
    matchPaths: [ROUTES.COMMISSION_RULES.BASE, ROUTES.COMMISSION_RULES.CREATE],
    permission: { module_name: "Commission Rules", permission: "View" },
  },
  {
    label: "Target",
    path: ROUTES.TARGET.BASE,
    icon: Target,
    matchPaths: [ROUTES.TARGET.BASE, ROUTES.TARGET.CREATE],
    permission: { module_name: "Target", permission: "View" },
  },
  {
    label: "Partners",
    path: ROUTES.PARTNER.BASE,
    icon: Partners,
    matchPaths: [
      ROUTES.PARTNER.BASE,
      ROUTES.PARTNER.CREATE,
      ROUTES.PARTNER.ID(":id").BASE,
      ROUTES.PARTNER.ID(":id").VIEW,
    ],
    permission: { module_name: "Partner", permission: "View" },
  },
  {
    label: "Run Payout",
    path: ROUTES.RUN_PAYOUT.BASE,
    icon: RunPayout,
    matchPaths: [ROUTES.RUN_PAYOUT.BASE],
    permission: { module_name: "Run Payout", permission: "View" },
  },
  {
    label: "Payouts Logs",
    path: ROUTES.PAYOUT_LOG.BASE,
    icon: PaymentLogs,
    matchPaths: [ROUTES.PAYOUT_LOG.BASE],
    permission: { module_name: "Payout Logs", permission: "View" },
  },
  {
    label: "Tenant Subscriptions",
    path: ROUTES.TENANT_SUBSCRIPTIONS.BASE,
    icon: () => <ReceiptLongOutlinedIcon sx={{ color: "#696E7B", fontWeight: "400" }} />,
    matchPaths: [ROUTES.TENANT_SUBSCRIPTIONS.BASE],
    permission: { module_name: "Tenant Subscriptions", permission: "View" },
  },
  {
    label: "Model Wise Configuration",
    path: ROUTES.MODEL_WISE_CONFIGURATION.BASE,
    icon: () => <ReceiptLongOutlinedIcon sx={{ color: "#696E7B", fontWeight: "400" }} />,
    matchPaths: [
      ROUTES.MODEL_WISE_CONFIGURATION.BASE,
      ROUTES.MODEL_WISE_CONFIGURATION.CREATE,
    ],
    permission: { module_name: "Model Wise Configuration", permission: "View" },
  },
  {
    label: "Coupons",
    path: ROUTES.COUPON.BASE,
    icon: () => <DiscountOutlinedIcon sx={{ color: "#6E737F", fontWeight: 400 }} />,
    matchPaths: [
      ROUTES.COUPON.BASE,
      ROUTES.COUPON.CREATE,
      `${ROUTES.COUPON.BASE}/:id`,
    ],
    permission: { module_name: "Coupon", permission: "View" },
  },
  // {
  //   label: "Settings",
  //   path: ROUTES.SETTINGS.BASE,
  //   icon: Setting,
  //   matchPaths: [ROUTES.SETTINGS.BASE],
  //   permission: { module_name: "Settings", permission: "View" },
  // },
];

const MOBILE_BREAKPOINT = 900;

const AppLayout = () => {
  const userDetails = useUserDetails();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );

  const toggleMobileDrawer = () => setMobileOpen((v) => !v);
  const toggleSidebar = () => setSidebarOpen((v) => !v);

  useEffect(() => {
    const setVhVar = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--app-vh", `${vh}px`);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      setVhVar();
    };

    setVhVar();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col">
      <div className="flex overflow-hidden flex-1 min-h-0 relative">
        <Sidebar
          mobileOpen={mobileOpen}
          handleDrawerToggle={toggleMobileDrawer}
          sidebarOpen={sidebarOpen}
          isMobile={isMobile}
          bannerOffset={0}
          navItems={navItems}
          removeAuthData={removeAuthData}
          user={userDetails}
        />

        <div className="flex flex-col flex-1 overflow-hidden transition-[margin-left] duration-200 ease-in-out">
          <div className="h-16 shrink-0 bg-sidebar">
            <Header
              onDrawerToggle={toggleMobileDrawer}
              onDrawerCollapse={toggleSidebar}
              sidebarOpen={sidebarOpen}
              user={userDetails}
              navigateRoutes={{
                PROFILE: ROUTES.PROFILE.BASE,
                SETTING: ROUTES.SETTINGS.BASE,
              }}
              myAccount="My Account"
            />
          </div>

          <main className="flex-1 m-2 pt-0 px-(--spacing-3) pb-(--spacing-3) overflow-hidden">
            <div className="relative h-full bg-neutral-50 dark:bg-[#2e2e2e] border border-neutral-200 rounded-[1.25rem] overflow-hidden flex flex-col">
              <div className="flex-1 p-5 overflow-auto custom-scrollbar">
                <Suspense fallback={<Loader type="content" />}>
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;